"use client";

import React, { useState, useMemo, useCallback, useEffect, useRef, Suspense } from "react";
import ClassFilterBar from "@/components/common/ClassFilterBar";
import ClassList from "@/components/common/ClassList";
import ClassCardSkeleton from "@/components/common/ClassCardSkeleton";
import NoResultsFound from "@/components/common/NoResultsFound";
import PageHeaderWithStats from "@/components/common/PageHeaderWithStats";
import AISmartSearch from "@/components/ai/AISmartSearch";
import { FitnessClass, Difficulty, InstructorType } from "@/types/fitness-class";
import {
  AcademicCapIcon,
  UserGroupIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowRightIcon,
  XMarkIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { SparklesIcon as SparklesSolid } from "@heroicons/react/24/solid";
import {
  CLASSES_PER_PAGE,
  CLASS_EXPERT_CATEGORIES,
} from "@/config/class-config";
import { useUserBookings } from "@/hooks/useUserBookings";
import { useQueryClient } from "@tanstack/react-query";
import { Instructor } from "@/types/instructor";
import { useClassesWithFilters } from "@/hooks/useClassesWithFilters";
import { useClassFilters, SORT_OPTIONS, SortOption } from "@/hooks/useClassFilters";
import { useDebounce } from "@/hooks/useDebounce";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { usePaymentSuccess } from "@/hooks/usePaymentSuccess";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { Footer } from "@/components/Footer";
import { useSearchParams } from "next/navigation";
import {
  getCapacityValues,
  getPriceFromPricing,
  getDurationFromSchedule,
  getTimeFromSchedule,
} from "@/lib/class-helpers";

const classesPerPage = CLASSES_PER_PAGE;
const expertCategories = CLASS_EXPERT_CATEGORIES;

function ClassesPageContent() {
  const queryClient = useQueryClient();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  // AI Smart Search state
  const searchParams = useSearchParams();
  // Use a SEPARATE URL param "ai_search" so it never collides with the regular "search" filter param
  const aiSearchParam = searchParams.get("ai_search");
  const [isAIMode, setIsAIMode] = useState(!!aiSearchParam);
  const [aiResults, setAiResults] = useState<FitnessClass[] | null>(null);

  // Store the AI query synchronously on first render so AISmartSearch has it immediately.
  // We use a ref so it survives re-renders but doesn't cause them.
  const initialAiQuery = useRef<string | null>(aiSearchParam);

  // When a NEW ai_search param arrives (e.g. user navigates from homepage again),
  // enable AI mode and capture the query. No need to strip the URL ourselves —
  // the useClassFilters hook will naturally replace the URL with filter-only params within 300ms.
  useEffect(() => {
    if (aiSearchParam && aiSearchParam !== initialAiQuery.current) {
      // A new AI query arrived (user navigated from homepage again while already on /classes)
      initialAiQuery.current = aiSearchParam;
      setIsAIMode(true);
    }
  }, [aiSearchParam]);

  // AI search results handler
  const handleAIResultsChange = useCallback(
    (classes: FitnessClass[] | null, stillInAIMode: boolean) => {
      setAiResults(classes);
      if (!stillInAIMode) {
        console.log("🔄 Disabling AI mode, showing regular classes");
        setIsAIMode(false);
        setAiResults(null);
      }
    },
    []
  );


  // Custom hooks — filters are now URL-synced
  const {
    filters,
    setFilters,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    resetFilters,
  } = useClassFilters();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Performance monitoring
  usePerformanceMonitor("ClassesPage");

  // Payment success handling
  usePaymentSuccess();

  // Use React Query hooks
  const { data: userBookedClasses = new Set<string>() } = useUserBookings();

  // Map sortBy to API params
  const { apiSortBy, apiSortOrder } = useMemo(() => {
    switch (sortBy) {
      case "price-low":
        return { apiSortBy: "price", apiSortOrder: "asc" };
      case "price-high":
        return { apiSortBy: "price", apiSortOrder: "desc" };
      case "rating":
        return { apiSortBy: "rating", apiSortOrder: "desc" };
      case "closest-date":
        return { apiSortBy: "date", apiSortOrder: "asc" };
      case "newest":
      default:
        return { apiSortBy: "createdAt", apiSortOrder: "desc" };
    }
  }, [sortBy]);

  const {
    data: infiniteData,
    isLoading,
    error: queryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useClassesWithFilters({
    limit: classesPerPage,
    sortBy: apiSortBy,
    sortOrder: apiSortOrder,
    filters,
  });

  // Flatten all pages into a single array
  const classes = useMemo(() => {
    return infiniteData?.pages.flatMap((page) => page.classes) || [];
  }, [infiniteData]);

  const totalClasses = infiniteData?.pages[0]?.pagination?.total || 0;
  const hasMore = hasNextPage || false;
  const error = queryError ? (queryError as Error).message : null;

  // Client-side filtering for immediate feedback on filters not handled server-side
  const filteredClasses = useMemo(() => {
    const filteredResult = classes.filter((fitnessClass: FitnessClass) => {
      // Search term filter
      if (debouncedSearchTerm.trim()) {
        const searchTermLower = debouncedSearchTerm.toLowerCase();
        const matchesSearch =
          fitnessClass.title?.toLowerCase().includes(searchTermLower) ||
          fitnessClass.instructorName
            ?.toLowerCase()
            .includes(searchTermLower) ||
          fitnessClass.category?.toLowerCase().includes(searchTermLower) ||
          fitnessClass.description?.toLowerCase().includes(searchTermLower) ||
          fitnessClass.difficulty?.some((d: Difficulty) =>
            d.toLowerCase().includes(searchTermLower)
          ) ||
          (fitnessClass.tags &&
            fitnessClass.tags.some((tag: string) =>
              tag.toLowerCase().includes(searchTermLower)
            ));

        if (!matchesSearch) {
          return false;
        }
      }

      // Price range filter (client-side)
      if (filters.price && (filters.price[0] > 0 || filters.price[1] < 5000)) {
        const price = getPriceFromPricing(fitnessClass);
        if (price < filters.price[0] || price > filters.price[1]) {
          return false;
        }
      }

      // Difficulty filter (client-side backup)
      if (filters.difficulty && filters.difficulty !== "all") {
        if (
          !fitnessClass.difficulty?.includes(filters.difficulty as Difficulty)
        ) {
          return false;
        }
      }

      // Duration filter (client-side)
      if (filters.duration && filters.duration > 0) {
        const duration = getDurationFromSchedule(fitnessClass);
        const filterDuration =
          typeof filters.duration === "string"
            ? parseInt(filters.duration)
            : filters.duration;

        if (duration !== filterDuration) {
          return false;
        }
      }

      // Time of day filter (client-side)
      if (filters.timeOfDay) {
        const time = getTimeFromSchedule(fitnessClass);
        if (!time || time === "") {
          return false;
        }

        const hour = parseInt(time.split(":")[0]);
        let timeOfDay = "";

        if (hour >= 5 && hour < 12) timeOfDay = "morning";
        else if (hour >= 12 && hour < 17) timeOfDay = "afternoon";
        else if (hour >= 17 && hour < 21) timeOfDay = "evening";
        else timeOfDay = "night";

        if (timeOfDay.toLowerCase() !== filters.timeOfDay.toLowerCase()) {
          return false;
        }
      }

      // Rating filter (client-side)
      if (filters.rating && (filters.rating[0] > 0 || filters.rating[1] < 5)) {
        const rating = fitnessClass.rating || 0;
        if (rating < filters.rating[0] || rating > filters.rating[1]) {
          return false;
        }
      }

      // Capacity filter (client-side)
      if (filters.capacity && filters.capacity > 0) {
        const { capacity } = getCapacityValues(fitnessClass);
        if (capacity < filters.capacity) {
          return false;
        }
      }

      // Special features filter (client-side)
      if (filters.specialFeatures && filters.specialFeatures.length > 0) {
        const classFeatures = fitnessClass.features || [];
        const hasFeature = filters.specialFeatures.some((feature: string) =>
          classFeatures.includes(feature)
        );
        if (!hasFeature) {
          return false;
        }
      }

      return true;
    });

    // Client-side sort for price / rating / date (since API may not fully support)
    if (sortBy === "price-low") {
      filteredResult.sort(
        (a, b) => getPriceFromPricing(a) - getPriceFromPricing(b)
      );
    } else if (sortBy === "price-high") {
      filteredResult.sort(
        (a, b) => getPriceFromPricing(b) - getPriceFromPricing(a)
      );
    } else if (sortBy === "rating") {
      filteredResult.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }

    // Add booking status to filtered classes
    return filteredResult.map((cls: FitnessClass) => {
      const classId = cls.id || "";
      const isBooked = classId ? userBookedClasses.has(classId) : false;
      return {
        ...cls,
        isBooked,
        ...(classId &&
          userBookedClasses.has(classId) && {
          bookingStatus: "confirmed",
          paymentStatus: "paid",
        }),
      };
    });
  }, [classes, filters, debouncedSearchTerm, userBookedClasses, sortBy]);

  // Use filtered classes directly
  const sortedClasses = filteredClasses;

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.category && filters.category !== "all") count++;
    if (filters.level && filters.level !== "all") count++;
    if (filters.difficulty && filters.difficulty !== "all") count++;
    if (filters.instructor) count++;
    if (filters.instructorType && filters.instructorType !== "all") count++;
    if (filters.format && filters.format !== "all") count++;
    if (filters.classType && filters.classType !== "all") count++;
    if (filters.ageGroup && filters.ageGroup !== "all") count++;
    if (filters.classLanguages && filters.classLanguages.length > 0) count++;
    if (filters.location) count++;
    if (filters.duration && filters.duration > 0) count++;
    if (filters.timeOfDay) count++;
    if (filters.price && (filters.price[0] > 0 || filters.price[1] < 5000))
      count++;
    if (filters.rating && (filters.rating[0] > 0 || filters.rating[1] < 5))
      count++;
    if (filters.capacity && filters.capacity > 0) count++;
    if (filters.specialFeatures && filters.specialFeatures.length > 0) count++;
    return count;
  }, [filters]);

  // Calculate the total available classes based on filtering
  const totalAvailableClasses = useMemo(() => {
    return activeFiltersCount > 0 || debouncedSearchTerm.trim()
      ? filteredClasses.length
      : totalClasses;
  }, [
    activeFiltersCount,
    debouncedSearchTerm,
    filteredClasses.length,
    totalClasses,
  ]);

  // Infinite scroll - fetch next page when scrolling
  const loadMoreRef = useInfiniteScroll(
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    { hasMore: hasNextPage || false, isLoading: isFetchingNextPage }
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px]">
      {/* Header and Stats Section */}
      <PageHeaderWithStats
        badge={{ text: "Fitness Classes", showPulse: true }}
        title={{ main: "Find Your Perfect", highlighted: "Class" }}
        description="Explore a wide variety of fitness and wellness classes designed to strengthen your body, calm your mind, and elevate your spirit."
        stats={[
          {
            icon: CalendarIcon,
            value: totalAvailableClasses,
            label: "Total Classes",
          },
          {
            icon: UserGroupIcon,
            value: classes.reduce((acc: number, cls: FitnessClass) => {
              const { capacity } = getCapacityValues(cls);
              return acc + capacity;
            }, 0),
            label: "Total Capacity",
          },
          {
            icon: AcademicCapIcon,
            value: new Set(
              classes.map((cls: FitnessClass) => cls.instructorName)
            ).size,
            label: "Instructors",
          },
          {
            icon: FunnelIcon,
            value: new Set(classes.map((cls: FitnessClass) => cls.category))
              .size,
            label: "Categories",
          },
        ]}
        loading={isLoading}
      />

      {/* Search, Filter and Sort Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6 sm:mb-8 zoom-container">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Search Bar — AI or Regular */}
          <div className="relative w-full flex-1">
            {/* AI Smart Search - Always mounted, visibility controlled by isAIMode */}
            <div style={{ display: isAIMode ? 'block' : 'none' }}>
              <AISmartSearch
                onResultsChange={handleAIResultsChange}
                isAIMode={isAIMode}
                onToggleAIMode={(enabled) => {
                  setIsAIMode(enabled);
                  if (!enabled) {
                    // Switching from AI to Regular — clear regular search so user starts fresh
                    setSearchTerm("");
                  }
                }}
                initialQuery={initialAiQuery.current}
              />
            </div>

            {/* Regular Search - Show when not in AI mode */}
            {!isAIMode && (
              <div className="relative flex flex-row items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <input
                    type="text"
                    placeholder="Search classes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 sm:px-6 py-3 sm:py-4 pl-10 sm:pl-14 pr-12 sm:pr-16 rounded-xl sm:rounded-2xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm text-sm sm:text-base text-gray-700 placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 shadow-lg hover:shadow-xl"
                  />
                  <MagnifyingGlassIcon className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400 absolute left-3 sm:left-5 top-1/2 transform -translate-y-1/2" />
                  <button className="absolute right-1.5 top-1/2 transform -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white p-1.5 sm:p-2 rounded-lg sm:rounded-xl transition-colors duration-300 group">
                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                  </button>
                </div>

                {/* AI Toggle Button */}
                <button
                  id="ai-search-toggle"
                  onClick={() => {
                    setSearchTerm(""); // Clear regular search before switching
                    setIsAIMode(true);
                  }}
                  className="group relative flex-shrink-0 flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-3 sm:py-4 rounded-xl sm:rounded-2xl border-2 border-purple-200 bg-gradient-to-r from-violet-50 to-purple-50 text-purple-700 hover:from-violet-100 hover:to-purple-100 hover:border-purple-300 hover:shadow-lg hover:shadow-purple-200/40 transition-all duration-300 shadow-lg"
                  title="Switch to AI-powered search"
                >
                  <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:text-purple-600 transition-colors" />
                  <span className="hidden sm:inline text-sm font-medium">AI Search</span>
                  <span className="sm:hidden text-[10px] font-bold">AI</span>
                  {/* Animated glow dot */}
                  <span className="absolute -top-1 -right-1 flex h-2 w-2 sm:h-3 sm:w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 sm:h-3 sm:w-3 bg-purple-500"></span>
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Category, Sort, Filter Controls — only in regular mode */}
          {!isAIMode && (
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto lg:flex-shrink-0">
              {/* Category Filter Dropdown */}
              {/* <div className="relative w-full sm:w-56">
                <button
                  type="button"
                  onClick={() =>
                    setIsCategoryDropdownOpen(!isCategoryDropdownOpen)
                  }
                  className="w-full appearance-none px-4 sm:px-6 py-3 sm:py-4 pr-10 rounded-xl sm:rounded-2xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base cursor-pointer text-left"
                >
                  {expertCategories.find(
                    (cat) => cat.apiFilter === filters.instructorType
                  )?.name || "All Classes"}
                </button>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform ${isCategoryDropdownOpen ? "rotate-180" : ""}`}
                />

                {isCategoryDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsCategoryDropdownOpen(false)}
                    />
                    <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                      {expertCategories.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              instructorType: category.apiFilter as
                                | "all"
                                | InstructorType,
                            }));
                            setIsCategoryDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 ${filters.instructorType === category.apiFilter
                            ? "bg-orange-50 text-orange-600 font-medium"
                            : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                            }`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div> */}

              {/* Sort Dropdown */}


              {/* Filter + Clear */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className={`inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 text-sm sm:text-base font-medium shadow-lg hover:shadow-xl relative ${activeFiltersCount > 0 ? "flex-1 sm:flex-none" : "w-full sm:w-auto"} ${isFilterOpen
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white/90 backdrop-blur-sm text-gray-700 border-gray-200 hover:border-orange-500 hover:text-orange-500"
                    }`}
                >
                  <FunnelIcon className="w-5 h-5" />
                  <span>Filters</span>
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>

                {activeFiltersCount > 0 && (
                  <button
                    onClick={resetFilters}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-xl sm:rounded-2xl border-2 border-red-200 bg-white/90 backdrop-blur-sm text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-300 text-sm sm:text-base font-medium shadow-lg hover:shadow-xl flex-shrink-0"
                    title="Clear all filters"
                  >
                    <XMarkIcon className="w-5 h-5" />
                    <span className="hidden sm:inline">Clear</span>
                  </button>
                )}
              </div>


              {/* <div className="relative w-full sm:w-52">
                <button
                  type="button"
                  onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  className="w-full appearance-none px-4 sm:px-5 py-3 sm:py-4 pr-10 rounded-xl sm:rounded-2xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base cursor-pointer text-left flex items-center gap-2"
                >
                  <BarsArrowDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="truncate">
                    {SORT_OPTIONS.find((o) => o.value === sortBy)?.label ||
                      "Sort"}
                  </span>
                </button>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform ${isSortDropdownOpen ? "rotate-180" : ""}`}
                />

                {isSortDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsSortDropdownOpen(false)}
                    />
                    <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                      {SORT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setSortBy(option.value);
                            setIsSortDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 text-sm ${sortBy === option.value
                            ? "bg-orange-50 text-orange-600 font-medium"
                            : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div> */}
            </div>
          )}
        </div>

        {/* Advanced Filters Modal */}
        {isFilterOpen && (
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-y-auto animate-slideDown">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FunnelIcon className="w-6 h-6 text-orange-500" />
                  Filter Classes
                </h2>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <ClassFilterBar
                  setFilters={setFilters}
                  filters={filters}
                  setCurrentPage={() => { }} // No-op: infinite query resets automatically on filter change
                />
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between rounded-b-2xl">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Reset All
                </button>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="px-6 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors shadow-md"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 sm:px-6 zoom-container">
        {/* ── AI Mode Results ── */}
        {isAIMode && aiResults && (
          <>
            {/* AI Results Count */}
            <div className="mb-4 sm:mb-6 flex items-center justify-between">
              <p className="text-gray-600 text-xs sm:text-sm flex items-center gap-1.5">
                <SparklesSolid className="w-4 h-4 text-purple-500" />
                Showing {aiResults.length} AI-matched {aiResults.length === 1 ? "class" : "classes"}
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-3 py-1 font-medium">
                <SparklesIcon className="w-3.5 h-3.5" />
                AI Ranked
              </span>
            </div>

            {/* AI Results List */}
            <div className="my-2">
              {aiResults.length > 0 ? (
                <ClassList
                  classes={aiResults}
                  mobileLayout="horizontal"
                  userBookedClasses={userBookedClasses}
                  onBookingSuccess={async () => {
                    await queryClient.invalidateQueries({
                      queryKey: ["userBookings"],
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ["classes"],
                    });
                  }}
                  onSpotUpdate={() => { }}
                />
              ) : (
                <div className="text-center py-12">
                  <SparklesIcon className="w-12 h-12 text-purple-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No matching classes found
                  </h3>
                  <p className="text-gray-500 mb-4 max-w-md mx-auto">
                    Try describing what you&apos;re looking for differently, or switch to regular search.
                  </p>
                  <button
                    onClick={() => { setIsAIMode(false); setAiResults(null); }}
                    className="px-5 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                  >
                    Switch to Regular Search
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Regular Mode Results ── */}
        {!isAIMode && (
          <>
            {/* Classes Count + Sort Badge */}
            {!isLoading && (
              <div className="mb-4 sm:mb-6 flex items-center justify-between">
                <p className="text-gray-600 text-xs sm:text-sm">
                  Showing {sortedClasses.length} of {totalAvailableClasses} classes
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 text-orange-600">
                      ({activeFiltersCount} {activeFiltersCount === 1 ? "filter" : "filters"} active)
                    </span>
                  )}
                </p>
                {sortBy !== "newest" && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-full px-3 py-1 font-medium">
                    <BarsArrowDownIcon className="w-3.5 h-3.5" />
                    {SORT_OPTIONS.find((o) => o.value === sortBy)?.label}
                  </span>
                )}
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
                <p className="text-red-600 text-center">{error}</p>
                <button
                  onClick={() => refetch()}
                  className="mt-2 mx-auto block px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Loading Skeleton State */}
            {isLoading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 p-2 sm:p-4 my-2">
                <ClassCardSkeleton mobileLayout="horizontal" count={8} />
              </div>
            )}

            {/* Classes List */}
            {!isLoading && (
              <div className="my-2">
                {sortedClasses.length > 0 ? (
                  <ClassList
                    classes={sortedClasses}
                    mobileLayout="horizontal"
                    userBookedClasses={userBookedClasses}
                    onBookingSuccess={async () => {
                      await queryClient.invalidateQueries({
                        queryKey: ["userBookings"],
                      });
                      await queryClient.invalidateQueries({
                        queryKey: ["classes"],
                      });
                    }}
                    onSpotUpdate={() => { }}
                  />
                ) : (
                  <NoResultsFound
                    searchTerm={searchTerm}
                    activeFiltersCount={activeFiltersCount}
                    onReset={resetFilters}
                  />
                )}
              </div>
            )}

            {/* Loading More State */}
            {isFetchingNextPage && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 p-2 sm:p-4 my-4">
                <ClassCardSkeleton mobileLayout="horizontal" count={4} />
              </div>
            )}

            {/* Load More Trigger */}
            <div ref={loadMoreRef} className="h-px" />

            {/* End Message */}
            {!hasMore && !isLoading && classes.length > 0 && (
              <div className="mt-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full mb-4">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  You&apos;ve reached the end!
                </h3>
                <p className="text-gray-600 mb-4">
                  No more classes to load. Try adjusting your filters to see more
                  results.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

// Wrap in Suspense for useSearchParams
const ClassesPage: React.FC = () => (
  <Suspense
    fallback={
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px] flex items-center justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 p-2 sm:p-4 max-w-7xl mx-auto w-full">
          <ClassCardSkeleton mobileLayout="horizontal" count={8} />
        </div>
      </div>
    }
  >
    <ClassesPageContent />
  </Suspense>
);

export default ClassesPage;
